import cx from "classnames";
import { produce } from "immer";
import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Text } from "@/components/elements/Text";
import { isStringNumber } from "@/utils/helpers";

import styles from "./styles.module.css";

// Inspiration from
// https://www.creativejuiz.fr/blog/en/tutorials/one-time-code-input-sms-code-keyboard-suggestion-ios-android

const setInputValue = (inputEl, value: string) => {
  // Set value to first item
  Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value"
  ).set.call(inputEl, value);
  // This will trigger a new render
  inputEl.dispatchEvent(new Event("change", { bubbles: true }));
};

const populateNext = (inputEl, data: string) => {
  // Set value to first item
  setInputValue(inputEl, data[0]);
  // remove the first char from data
  data = data.substring(1);
  inputEl.focus();
  const nextInput =
    inputEl.nextElementSibling && inputEl.nextElementSibling.tagName === "INPUT"
      ? inputEl.nextElementSibling
      : null;
  if (nextInput && data.length) {
    // Do the same with the next element and next data
    populateNext(nextInput, data);
  }
};

const splitNumber = (e) => {
  const data = e.target.value;
  // Shouldn't happen, just in case.
  if (!data) return;
  // Here is a normal behavior, not a paste action.
  if (data.length === 1) return;
  populateNext(e.target, data);
};

// Keyboard listeners
const onKeyup = (e) => {
  const { key, target } = e;
  const { value } = target;
  const previousInput =
    target.previousElementSibling &&
    target.previousElementSibling.tagName === "INPUT"
      ? target.previousElementSibling
      : null;
  const nextInput =
    target.nextElementSibling && target.nextElementSibling.tagName === "INPUT"
      ? target.nextElementSibling
      : null;
  // Ignore certain keys
  const keysToIgnore = ["Tab", "Shift", "Meta", "Alt", "Control"];
  if (keysToIgnore.includes(key)) return;
  // On left arrow or Backspace, go to the previous field.
  if ((key === "ArrowLeft" || key === "Backspace") && previousInput) {
    previousInput.select();
    return;
  }
  // Stop here if no next element
  if (!nextInput) return;
  // On right arrow go to next field
  if (key === "ArrowRight") {
    nextInput.select();
    return;
  }
  // On typing, go to the next field
  if (nextInput && key !== "Backspace" && value) {
    nextInput.select();
  }
  // If the target is populated to quickly
  if (value.length > 1) {
    splitNumber(e);
  }
};

// Focus listener
const onFocus = (e) => {
  const { target, value } = e;
  const { index: inputIndex } = target.dataset;
  // If the focus element is the first one, do nothing
  if (inputIndex === "0") return;
  // If value of input 1 is empty, focus it.
  if (inputIndex === "0" && value === "") {
    target.focus();
  }
};

const getAllInputs = (
  parentEl: HTMLFieldSetElement
): NodeListOf<HTMLInputElement> => {
  return parentEl.querySelectorAll('input[type="text"]');
};

const clearAllInputs = (parentEl: HTMLFieldSetElement): void => {
  const numberInputs = getAllInputs(parentEl);
  // Clear values
  [].forEach.call(numberInputs, function (el) {
    setInputValue(el, "");
  });
  // Focus first box
  numberInputs[0].focus();
};

export type InputVerificationCodeProps = {
  id?: string;
  hidden?: boolean;
  totalCharacters?: number;
  setCode: (code: string) => void;
  clearValues?: boolean;
  className?: string;
};

export const InputVerificationCode = ({
  id = "verification-code",
  hidden = false,
  totalCharacters = 4,
  setCode,
  clearValues = false,
  className,
}: InputVerificationCodeProps) => {
  const { t } = useTranslation();
  // Create placeholder arrays to generate labels and input fields
  const labelsArr = Array.from(Array(totalCharacters + 1), (x, i) => i);
  const inputsArr = Array.from(
    Array(Math.max(totalCharacters - 1, 0)),
    (x, i) => i
  );
  // Handle keypresses
  const parentRef = useRef<HTMLFieldSetElement>(null);
  const firstInputRef = useRef(null);
  useEffect(() => {
    const { current: parentEl } = parentRef;
    const { current: firstInputEl } = firstInputRef;
    if (!parentRef) return undefined;
    const numberInputs = getAllInputs(parentEl);
    // Handle paste on placeholder
    firstInputEl.addEventListener("input", splitNumber);
    // Listen to events on input
    [].forEach.call(numberInputs, function (el) {
      // Handle different keys on inputs
      el.addEventListener("keyup", onKeyup, false);
      el.addEventListener("focus", onFocus, false);
    });
    // Clean up
    return () => {
      firstInputEl.removeEventListener("input", splitNumber);
      [].forEach.call(numberInputs, function (el) {
        el.removeEventListener("keyup", onKeyup, false);
        el.removeEventListener("focus", onFocus, false);
      });
    };
  }, []);
  // Store values
  const initialValues = Array.from(Array(totalCharacters), () => "");
  const [values, setValues] = useState<string[]>(initialValues);

  const setValue = useCallback(
    (value: string, index: number) => {
      const newValues = produce(values, (draft) => {
        draft[index] = value;
      });
      setValues(newValues);
    },
    [values]
  );
  // Clear values (when parent errors)
  useEffect(() => {
    if (!clearValues) return;
    const { current: parentEl } = parentRef;
    clearAllInputs(parentEl);
  }, [clearValues]);

  // Test completeness
  const isComplete = useMemo(() => {
    return values.every((value) => value);
  }, [values]);

  // Test validity
  const isValid = useMemo(() => {
    if (!isComplete) return false;
    return values.every((value) => isStringNumber(value));
  }, [values, isComplete]);

  // Set code value on parent
  useEffect(() => {
    const code = isValid ? values.join("") : "";
    setCode(code);
  }, [isValid, values, setCode]);

  // FOCUS ON MOUNT
  useEffect(() => {
    const { current: input } = firstInputRef;
    input.focus();
  }, []);

  return (
    <>
      <fieldset
        ref={parentRef}
        className={cx(styles.fieldSet, className, { visuallyhidden: hidden })}
      >
        {/* ALL LABELS */}
        {labelsArr.map((index) => {
          const labelId = `${id}-${index}`;
          return (
            <label key={labelId} htmlFor={labelId} className="visuallyhidden">
              Number {index.toString()}
            </label>
          );
        })}
        {/* INPUT FIELDS */}
        <div className={styles.inputsContainer}>
          {/* FIRST INPUT */}
          <input
            id={`${id}-0`}
            name={`${id}-0`}
            type="text"
            pattern="[0-9]*"
            inputMode="numeric"
            data-index="0"
            autoComplete="one-time-code"
            ref={firstInputRef}
            className={styles.input}
            onChange={(e) => {
              setValue(e.target.value, 0);
            }}
          />
          {/* OTHER INPUTS */}
          {inputsArr.map((index) => {
            const indexAdjusted = index + 1;
            const inputId = `${id}-${indexAdjusted}`;
            return (
              <input
                key={indexAdjusted}
                id={inputId}
                name={inputId}
                type="text"
                pattern="[0-9]*"
                inputMode="numeric"
                data-index={indexAdjusted.toString()}
                className={styles.input}
                onChange={(e) => {
                  setValue(e.target.value, indexAdjusted);
                }}
              />
            );
          })}
        </div>
      </fieldset>
      {/* INPUT ERROR (not hooked up to react hook form so can't use InputError) */}
      <div className="inputErrorContainer">
        {isComplete && !isValid && (
          <Text as="span" size="xxs">
            {t("error_verification_code")}
          </Text>
        )}
      </div>
    </>
  );
};
